import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Package,
  Palette,
  ShoppingBag,
  SwatchBook,
  LogOut,
  Menu,
  X,
  Tag,
  Bookmark,
  CreditCard,
  Sparkles,
  BarChart3,
  Megaphone,
} from 'lucide-react';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/reports', icon: BarChart3, label: 'Reports' },
    { path: '/workshops', icon: Calendar, label: 'Workshops' },
    { path: '/reservations', icon: ClipboardList, label: 'Reservations' },
    { path: '/custom-orders', icon: Package, label: 'Custom Orders' },
    { path: '/paint-kits', icon: Palette, label: 'Paint Kits' },
    { path: '/paint-kit-orders', icon: ShoppingBag, label: 'Kit Orders' },
    { path: '/color-palettes', icon: SwatchBook, label: 'Color Palettes' },
    { path: '/color-palette-orders', icon: ShoppingBag, label: 'Palette Orders' },
    { path: '/categories', icon: Bookmark, label: 'Categories' },
    { path: '/collections', icon: Tag, label: 'Collections' },
    { path: '/payment-settings', icon: CreditCard, label: 'Payment Settings' },
    { path: '/promotion', icon: Sparkles, label: 'Discount / Promotion' },
    { path: '/announcement', icon: Megaphone, label: 'Announcement' },
  ];

  return (
    <div className={styles.layout}>
      {/* Mobile menu button */}
      <button
        className={styles.mobileMenuBtn}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <Palette size={32} />
          <div>
            <h1>Paint by Numbers</h1>
            <span>Admin Panel</span>
          </div>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
              onClick={() => setSidebarOpen(false)}
              end={item.path === '/'}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.adminInfo}>
            <div className={styles.adminAvatar}>
              {admin?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className={styles.adminDetails}>
              <span className={styles.adminName}>{admin?.name || 'Admin'}</span>
              <span className={styles.adminEmail}>{admin?.email}</span>
            </div>
          </div>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className={styles.main}>{children}</main>
    </div>
  );
}
